// constants.js — All app-wide constants

// ─── Stage Pipeline ────────────────────────────────────────────────────────
export const STAGES = [
  { label: 'Intake & Request',              short: 'Intake',    phase: 'pre' },
  { label: 'Procurement Method',            short: 'Method',    phase: 'pre' },
  { label: 'Document Drafting',             short: 'Drafting',  phase: 'pre' },
  { label: 'STD 215 Draft',                 short: 'STD 215',   phase: 'pre' },
  { label: 'Internal Review',               short: 'Review',    phase: 'pre' },
  { label: 'CalEProcure Posted',            short: 'Posted',    phase: 'sol'  },
  { label: 'Bid Period Open',               short: 'Bidding',   phase: 'sol'  },
  { label: 'SB/DVBE Compliance',            short: 'SB/DVBE',   phase: 'sol'  },
  { label: 'Bid Evaluation',                short: 'Eval',      phase: 'sol'  },
  { label: 'Award Decision',                short: 'Award',     phase: 'award'},
  { label: 'Union Notification',            short: 'Union',     phase: 'award'},
  { label: 'Protest Period',                short: 'Protest',   phase: 'award'},
  { label: 'Contract Execution',            short: 'Execute',   phase: 'exec' },
  { label: 'DGS/OLS Routing',               short: 'DGS/OLS',   phase: 'exec' },
  { label: 'Handoff & Closure',             short: 'Handoff',   phase: 'handoff'},
];

export const STAGE_PHASES = {
  pre:    { label: 'Pre-Solicitation', color: '#E8C97A', bg: '#2A1F05' },
  sol:    { label: 'Solicitation',     color: '#60A5FA', bg: '#0A1A3D' },
  award:  { label: 'Award',            color: '#F97316', bg: '#3D1A00' },
  exec:   { label: 'Execution',        color: '#34D399', bg: '#0A2D1A' },
  handoff: { label: 'Handoff',         color: '#34D399', bg: '#0A2D1A' },
};

export const STAGE_NEXT_TIPS = {
  0:  'Verify funding source and business justification before proceeding.',
  1:  'Confirm PAAL thresholds. Document procurement method selection rationale.',
  2:  'Draft SOW, Solicitation, Exhibit B, and Exhibit D concurrently. Use Penny for all documents. Have Legal review before posting.',
  3:  'STD 215 requires all fields. Confirm signatory authority.',
  4:  'Send to division management and Legal for concurrent review.',
  5:  'Monitor CalEProcure for questions. Issue addenda within 5 business days.',
  6:  'Log all bidder questions. Maintain confidentiality of respondents.',
  7:  'Document SB/DVBE participation goal, outreach efforts, and waiver status if applicable. Required before award.',
  8:  'Document evaluation criteria scores. Maintain bidder confidentiality.',
  9:  'Prepare award notification. Document selection rationale in writing.',
  10: 'GC 19131 requires notification to exclusive representative. Allow 10 days.',
  11: 'Protest period is 5 business days from award notification.',
  12: 'Both parties must sign before work begins. Route per approval matrix.',
  13: 'DGS/OLS review required above PAAL threshold. Include all exhibits.',
  14: 'Complete internal and contractor handoff packages. Send survey links. Contract file archived on completion.',
};

// ─── Default Data Structures ───────────────────────────────────────────────
export const DEFAULT_PAAL = {
  nonIT: 50000,
  purchasingAuthNum: '', // PAAL purchasing authority number
  it: 200000,
  notes: '',
  uploaded: false,
};

export const DEFAULT_PROFILE = {
  contactName: '',
  contactTitle: '',
  contactPhone: '',
  contactEmail: '',
  agencyName: 'Office of Energy Infrastructure Safety',
  agencyDivision: 'Admin Services Division',
  agencyBillingCode: '',
  accountingOfficerName: '',
  accountingOfficerTitle: '',
  accountingOfficerPhone: '',
  accountingOfficerEmail: '',
  signatoryName: '',
  signatoryTitle: '',
  signatoryPhone: '',
  signatoryEmail: '',
  scoAgencyCode: '3355', // SCO agency code — Energy Safety is 3355
  supervisorName: '',
  supervisorTitle: '',
  supervisorPhone: '',
  supervisorEmail: '',
  unionHrName: '',
  unionHrEmail: '',
  unionLrName: '',
  unionLrEmail: '',
  unionCcEmails: '',
};

export const DEFAULT_S215 = {
  agreementNum: '',
  amendmentNum: '',
  agencyBillingCode: '',
  scoId: '', // State Controller Office encumbrance/contract ID
  fundingLines: [], // [{id, encumbranceNum, fund, orgCode, objectCode, fiscalYear, amount}]
  startDate: '',
  endDate: '',
  maxAmount: '',
  contractorName: '',
  contractorTaxId: '',
  contractorAddress: '',
  contractorCity: '',
  contractorState: 'CA',
  contractorZip: '',
  contractorPhone: '',
  contractorEmail: '',
  contractorContactName: '',
  contractorContactTitle: '',
  contractorContactPhone: '',
  contractorContactEmail: '',
  procMethod: '',
  agreeOutline: '',
  justification547: '',
  contactName: '',
  contactTitle: '',
  contactPhone: '',
  contactEmail: '',
  accountingOfficerName: '',
  accountingOfficerTitle: '',
  accountingOfficerPhone: '',
  accountingOfficerEmail: '',
  signatoryName: '',
  signatoryTitle: '',
  signatoryPhone: '',
  signatoryEmail: '',
  scoAgencyCode: '3355', // SCO agency code — Energy Safety is 3355
  supervisorName: '',
  supervisorTitle: '',
  supervisorPhone: '',
  supervisorEmail: '',
  unionHrName: '',
  unionHrEmail: '',
  unionLrName: '',
  unionLrEmail: '',
  unionCcEmails: '',
  dgsRouted: false,
  dgsRoutedDate: '',
  dgsApproved: false,
  dgsApprovedDate: '',
  unionNotified: false,
  unionNotifiedDate: '',
};

export const DEFAULT_CONTRACT = (profile = {}, paal = DEFAULT_PAAL) => ({
  id: uid(),
  title: '',
  contractNum: '', // Energy Safety internal contract number (assigned at intake)
  description: '',
  budget: '',
  isIT: 'no',
  procMethod: '',
  category: 'Professional Services',
  notes: '',
  stage: 0,
  stageHistory: [{ stage: 0, date: today(), note: 'Contract created' }],
  createdAt: today(),
  updatedAt: today(),
  s215: {
    ...DEFAULT_S215,
    contactName: profile.contactName || '',
    contactTitle: profile.contactTitle || '',
    contactPhone: profile.contactPhone || '',
    contactEmail: profile.contactEmail || '',
    accountingOfficerName: profile.accountingOfficerName || '',
    accountingOfficerTitle: profile.accountingOfficerTitle || '',
    accountingOfficerPhone: profile.accountingOfficerPhone || '',
    accountingOfficerEmail: profile.accountingOfficerEmail || '',
    signatoryName: profile.signatoryName || '',
    signatoryTitle: profile.signatoryTitle || '',
    signatoryPhone: profile.signatoryPhone || '',
    signatoryEmail: profile.signatoryEmail || '',
    agencyBillingCode: profile.agencyBillingCode || '',
  },
  exhibits: [],
  exhibitNumbering: 'alpha',
  attachments: [],
  amendments: [],
  reminders: [],
  documents: { rec: [], sow: [], sol: [], std215: [], other: [] },
  exemption: {},
  otaExemption: {},
  dvbe: {},
  pennyDrafts: {},
});

// ─── Reference Library ────────────────────────────────────────────────────
export const REF_DOCS = [
  { key: 'scm_vol1', label: 'SCM Vol 1 (Non-IT)', desc: 'State Contracting Manual Vol 1 — Non-IT services', type: 'ref' },
  { key: 'scm_vol2', label: 'SCM Vol 2 (IT)',     desc: 'State Contracting Manual Vol 2 — IT services',     type: 'ref' },
];

export const ALL_LIB = [...REF_DOCS];

// ─── Email Templates ──────────────────────────────────────────────────────
export const EMAIL_TYPES = {
  union: {
    label: 'Union Award Notification',
    desc: 'GC 19131 — Required notice to exclusive bargaining representative before contract execution. Allow 10 business days.',
    urgent: true,
    stage: 10,
    icon: '⚖',
    category: 'Required Notices',
  },
  intent_award: {
    label: 'Intent to Award',
    desc: 'Notice to all bidders announcing the intended awardee. Starts the 5-day protest period.',
    urgent: true,
    stage: 9,
    icon: '📢',
    category: 'Required Notices',
  },
  bid_rejection: {
    label: 'Bid Rejection Notice',
    desc: 'Formal notice to unsuccessful bidders informing them of the award decision.',
    urgent: false,
    stage: 9,
    icon: '📋',
    category: 'Required Notices',
  },
  award: {
    label: 'Notice of Award',
    desc: 'Formal notice to the successful contractor confirming award and next steps for execution.',
    urgent: false,
    stage: 9,
    icon: '✅',
    category: 'Contractor Correspondence',
  },
  execution_notice: {
    label: 'Contract Execution Notice',
    desc: 'Notice to contractor that the contract is fully executed and work may begin.',
    urgent: false,
    stage: 12,
    icon: '📝',
    category: 'Contractor Correspondence',
  },
  amendment: {
    label: 'Amendment Notification',
    desc: 'Notifies contractor and stakeholders of a contract amendment — scope, budget, or term change.',
    urgent: false,
    stage: null,
    icon: '🔄',
    category: 'Contractor Correspondence',
  },
  dgs: {
    label: 'DGS/OLS Routing Cover Letter',
    desc: 'Cover letter for DGS routing package per PCC §10295.',
    urgent: false,
    stage: 13,
    icon: '🏛',
    category: 'Internal & Agency',
  },
  requestor: {
    label: 'Requestor Award Notification',
    desc: 'Internal memo to the requesting division notifying them of the award decision.',
    urgent: false,
    stage: 9,
    icon: '📩',
    category: 'Internal & Agency',
  },
  protest_response: {
    label: 'Protest Response',
    desc: 'Formal response to a bid protest — acknowledges receipt, outlines review process.',
    urgent: false,
    stage: 11,
    icon: '⚠',
    category: 'Internal & Agency',
  },
};

// ─── Document Types (Vault) ───────────────────────────────────────────────
export const DOC_TYPES = {
  rec:    'Procurement Recommendation',
  sow:    'Scope of Work (Exhibit A)',
  sol:    'Solicitation Document',
  std215: 'STD 215',
  other:  'Other Documents',
};

// ─── Contract Term (general length, selected at intake — exact start/end
//   dates are captured later in the process, e.g. STD 215) ────────────────
export const CONTRACT_TERMS = [
  { value: 'lt1yr', label: 'Less than 1 year', years: 0.5 },
  { value: '1yr',   label: '1 year',           years: 1 },
  { value: '2yr',   label: '2 years',          years: 2 },
  { value: '3yr',   label: '3 years',          years: 3 },
  { value: 'other', label: 'Other',            years: null },
];

// ─── Procurement Methods ──────────────────────────────────────────────────
export const PROC_METHODS = [
  { value: 'IFB',           label: 'IFB — Invitation for Bid' },
  { value: 'RFQ',           label: 'RFQ — Request for Quotation' },
  { value: 'RFO',           label: 'RFO — Request for Offer' },
  { value: 'RFP-Primary',   label: 'RFP — Primary Request for Proposal' },
  { value: 'RFP-Secondary', label: 'RFP — Secondary Request for Proposal' },
  { value: 'NCB',           label: 'NCB — Non-Competitive Bid' },
  { value: 'Sole Source',   label: 'Sole Source' },
  { value: 'SB/DVBE Option', label: 'SB/DVBE Option — two matching-type quotes, $5K–$249,999.99 (GC §14838.5, SCM 1 §5.80.B)' },
  { value: 'F&R',           label: 'F&R — Fair and Reasonable (goods under $20K, IT or non-IT, SCM Vol 2 §1510)' },
  // Non-IT low-dollar methods, ascending by threshold.
  // StatutoryExempt-5K: under $5K is a *statutory* exemption from competitive
  //   bidding (PCC §10335(a), §10335.5(c)(5)) — exempt by law, distinct from
  //   the categorical exemption below.
  // PriceQuote: $5K–$20K is the *categorical* exemption per SCM 1 §5.80.B.2.p
  //   (Rev 04/26) — not required to be advertised/bid; agency obtains one
  //   fair-and-reasonable price quotation (two if a single quote isn't
  //   demonstrably fair and reasonable). Threshold raised from $10K to $20K
  //   in the April 2026 SCM Vol 1 edition.
  // NOTE: the internal `value` strings are kept stable — existing contracts
  //   store procMethod by value, and App.jsx expectedKey matching depends on
  //   them. Only labels were updated.
  { value: 'StatutoryExempt-5K', label: 'Statutory Exemption — services under $5K (SCM 1, §5.80.A.1; PCC §10335(a))' },
  { value: 'PriceQuote',    label: 'Price Quotation — Non-IT services under $20K (SCM 1, §5.80.B.2.p)' },
  { value: 'Emergency',     label: 'Emergency Contract (SCM 1, §4.07)' },
  { value: 'UC-CSU',        label: 'UC/CSU/University Agreement (SCM 1, §3.06)' },
  { value: 'Exempt',        label: 'Exempt from Competitive Bidding' },
  { value: 'CMAS',          label: 'CMAS — California Multiple Award Schedules' },
  { value: 'CALNET',        label: 'CALNET — California Network (Telecom/Network Services)' },
  { value: 'SLP',           label: 'SLP — Software Licensing Program (DGS/OTech)' },
  { value: 'LPA-Other',     label: 'LPA — Other Leveraged Procurement Agreement' },
  { value: 'Other',         label: 'Other' },
];

// LPA methods — all use non-competitive ordering (no solicitation, no bid period, no eval)
export const LPA_METHODS = new Set(['CMAS', 'CALNET', 'SLP', 'LPA-Other']);
// Stages skipped/greyed for LPA methods (no competitive solicitation)
export const CMAS_SKIPPED_STAGES = new Set([5, 6, 8]); // kept for backward compat
export const LPA_SKIPPED_STAGES = new Set([5, 6, 8]); // CalEProcure Posted, Bid Period, Bid Evaluation

// ─── Utilities ────────────────────────────────────────────────────────────
export function uid() {
  // Generate a proper UUID v4 for Supabase compatibility
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

export function today() {
  return new Date().toISOString().split('T')[0];
}

export function fmtDate(d) {
  if (!d) return '—';
  try {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return d;
  }
}

export function fmtMoney(n) {
  const num = parseFloat(n);
  if (!num) return '—';
  if (num >= 1_000_000) return '$' + (num / 1_000_000).toFixed(1) + 'M';
  if (num >= 1_000) return '$' + Math.round(num / 1000) + 'K';
  return '$' + num.toLocaleString();
}

export function fmtMoneyFull(n) {
  const num = parseFloat(n);
  if (!num) return '—';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtSize(bytes) {
  if (!bytes) return '0 KB';
  if (bytes > 1_048_576) return (bytes / 1_048_576).toFixed(1) + ' MB';
  return Math.round(bytes / 1024) + ' KB';
}

export function calcHealthScore(contract, paal) {
  if (!contract || contract.archived) return null;
  let score = 100;
  const deductions = [];

  // Days in current stage (max -30 points)
  const lastStage = [...(contract.stageHistory || [])].reverse()[0];
  const days = lastStage ? Math.floor((new Date() - new Date(lastStage.date)) / (1000 * 60 * 60 * 24)) : 0;
  if (days > 30) { score -= 30; deductions.push('Stalled ' + days + ' days'); }
  else if (days > 14) { score -= 15; deductions.push(days + ' days in stage'); }

  // DGS routing not addressed (-20 points)
  const dgs = dgsCheck(contract, paal);
  if (dgs.required && contract.stage >= 12 && !contract.s215?.dgsRouted) {
    score -= 20; deductions.push('DGS routing pending');
  }

  // SB/DVBE compliance incomplete at stage 7+ (-15 points) — not applicable to CMAS (different process)
  if (contract.stage >= 7 && !LPA_METHODS.has(contract.procMethod) && (!contract.dvbe?.solMethod || !contract.dvbe?.sbGoal || !contract.dvbe?.dvbeRequired)) {
    score -= 15; deductions.push('SB/DVBE docs incomplete');
  }

  // No contract title (-10 points)
  if (!contract.title || contract.title.trim() === '') {
    score -= 10; deductions.push('No contract title');
  }

  // No budget (-10 points)
  if (!contract.budget || parseFloat(contract.budget) === 0) {
    score -= 10; deductions.push('No budget set');
  }

  // No procurement method (-10 points)
  if (!contract.procMethod) {
    score -= 10; deductions.push('No procurement method');
  }

  // Union notification pending at stage 10 (-5 points)
  if (contract.stage === 10 && !contract.s215?.unionNotified) {
    score -= 5; deductions.push('Union notice pending');
  }

  return { score: Math.max(0, score), deductions };
}

export function daysAgo(dateStr) {
  if (!dateStr) return null;
  const then = new Date(dateStr + 'T00:00:00');
  const now = new Date();
  return Math.floor((now - then) / 86_400_000);
}

export function dgsCheck(contract, paal) {
  const amt = parseFloat(contract.budget) || 0;
  const threshold = contract.isIT === 'yes' ? (paal.it || 200000) : (paal.nonIT || 50000);
  const isCompetitive = ['IFB', 'RFQ', 'RFO', 'RFP-Primary', 'RFP-Secondary'].includes(contract.procMethod);
  const exceedsAuthority = amt > threshold;

  // LPA methods (CMAS, CALNET, SLP, LPA-Other) — non-competitive ordering vehicles
  if (LPA_METHODS.has(contract.procMethod)) {
    const lpaLabel = contract.procMethod === 'CALNET' ? 'CALNET' : contract.procMethod === 'SLP' ? 'SLP' : contract.procMethod === 'LPA-Other' ? 'LPA' : 'CMAS';
    return {
      required: exceedsAuthority,
      olsRequired: exceedsAuthority,
      otaRequired: false,
      threshold,
      reason: exceedsAuthority
        ? `Budget ${fmtMoney(amt)} exceeds delegated authority of ${fmtMoney(threshold)}. DGS/OLS approval required. ${lpaLabel} orders above PAAL must be routed through DGS/OLS per PCC §10295.`
        : `Budget ${fmtMoney(amt)} is within the $${fmtMoney(threshold)} PAAL threshold. No DGS/OLS routing required for this ${lpaLabel} order within delegated authority.`,
    };
  }

  // OLS: contract approval required when budget exceeds PAAL threshold
  const olsRequired = exceedsAuthority;

  // OTA (One Time Acquisition): DGS IT procurement — only applies to IT contracts (SCM Vol 2).
  // Non-IT contracts (SCM Vol 1) that exceed PAAL require OLS only, never OTA.
  const otaRequired = exceedsAuthority && isCompetitive && contract.isIT === 'yes';

  if (!exceedsAuthority) {
    return {
      required: false,
      olsRequired: false,
      otaRequired: false,
      threshold,
      reason: `Budget ${fmtMoney(amt)} is within delegated authority of ${fmtMoney(threshold)}.`,
    };
  }

  // Both OTA and OLS — IT competitive contracts above PAAL threshold
  if (otaRequired) {
    return {
      required: true,
      olsRequired: true,
      otaRequired: true,
      threshold,
      reason: `Budget ${fmtMoney(amt)} exceeds delegated authority of ${fmtMoney(threshold)}. DGS/OTA (One Time Acquisition) must conduct the IT solicitation on your behalf, and DGS/OLS must approve the contract.`,
    };
  }

  // OLS only — non-IT contracts above PAAL, or non-competitive IT contracts (NCB, Sole Source, Emergency, etc.)
  return {
    required: true,
    olsRequired: true,
    otaRequired: false,
    threshold,
    reason: `Budget ${fmtMoney(amt)} exceeds delegated authority of ${fmtMoney(threshold)}. DGS/OLS approval required per PCC §10295.`,
  };
}

export function nextExLabel(exhibits, mode = 'alpha') {
  if (mode === 'numeric') {
    const nums = exhibits.map(e => parseInt(e.label)).filter(n => !isNaN(n));
    return String(nums.length > 0 ? Math.max(...nums) + 1 : 1);
  }
  const used = exhibits.map(e => e.label.toUpperCase());
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  return letters.find(l => !used.includes(l)) || 'Z';
}

export function buildContractListEntry(contract) {
  return {
    id: contract.id,
    title: contract.title,
    budget: contract.budget,
    stage: contract.stage,
    isIT: contract.isIT,
    procMethod: contract.procMethod,
    category: contract.category,
    notes: contract.notes,
    contractNum: contract.contractNum || '',
    createdAt: contract.createdAt,
    updatedAt: contract.updatedAt,
    contractorName: contract.s215?.contractorName || '',
    agreementNum: contract.s215?.agreementNum || '',
    // Preserve full data so openContract gets complete record
    s215: contract.s215 || {},
    exhibits: contract.exhibits || [],
    exhibitNumbering: contract.exhibitNumbering || 'alpha',
    attachments: contract.attachments || [],
    amendments: contract.amendments || [],
    reminders: contract.reminders || [],
    documents: contract.documents || {},
    referenceDocs: contract.referenceDocs || [],
    assignedCmId: contract.assignedCmId,
    exemption: contract.exemption || {},
    otaExemption: contract.otaExemption || {},
    dvbe: contract.dvbe || {},
    sbDvbeCertNum: contract.sbDvbeCertNum || '',
    contractorVendorId: contract.contractorVendorId || null,
    pennyDrafts: contract.pennyDrafts || {},
    general_attachments: contract.general_attachments || [],
    std213: contract.std213 || {},
    handoff: contract.handoff || {},
    bidEval: contract.bidEval || {},
    calEProcure: contract.calEProcure || {},
    stageHistory: contract.stageHistory || [],
    analystName: contract.analystName || '',
    analystEmail: contract.analystEmail || '',
    archived: contract.archived || false,
  reviewStatus: contract.reviewStatus || null,
  reviewedBy: contract.reviewedBy || null,
  reviewedAt: contract.reviewedAt || null,
    archivedAt: contract.archivedAt || null,
  };
}

export function todayStr() {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

// ─── Contract Template Library ───────────────────────────────────────────
export const CONTRACT_TYPES = [
  'Consulting',
  'Personal Services',
  'IT Services',
  'Inspections',
  'Training & Development',
  'Professional Services',
  'Maintenance & Operations',
  'Other',
];

export const EXHIBIT_TAGS = [
  { key: 'A', label: 'Exhibit A — Scope of Work' },
  { key: 'B', label: 'Exhibit B — Payment Terms' },
  { key: 'C', label: 'Exhibit C — General Terms & Conditions (02/2025)' },
  { key: 'D', label: 'Exhibit D — Special Terms and Conditions' },
];
